import { memo } from 'preact/compat'
import { useLocation } from 'preact-iso'
import type { DashboardCampaignPerformanceItemDto, CampaignTrendPointDto } from '@/types/dashboard'
import { LineChart } from 'lucide-preact'
import { useCompany } from '@/hooks/useCompany'
import { routes } from '@/utils/routes'
import { rowActivation } from '@/utils/rowActivation'

interface CampaignPerformanceTileProps {
    campaigns: DashboardCampaignPerformanceItemDto[] | null | undefined
    workspaceId: string
    className?: string
    loading?: boolean
    error?: string | null
    /** The server could not compute this section — shown as broken, not as empty. */
    failed?: boolean
    /** Suppresses navigation while the dashboard is showing mock data. */
    mock?: boolean
}

// Simple sparkline component (always upward cumulative) without axes or labels.
function Sparkline({ points }: { points: CampaignTrendPointDto[] }) {
    if (!points || points.length === 0) {
        return <div className="h-10 w-32 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center text-2xs text-slate-400 dark:text-slate-500">No trend</div>
    }
    // Normalize to SVG coordinates.
    const w = 120
    const h = 40
    const values = points.map(p => p.CumulativeLeadCount)
    const max = Math.max(...values)
    const min = Math.min(...values)
    const range = max - min || 1
    const stepX = w / Math.max(points.length - 1, 1)
    const path = points.map((p, i) => {
        const x = i * stepX
        const y = h - ((p.CumulativeLeadCount - min) / range) * (h - 4) - 2 // 2px padding top/bottom
        return `${x},${y}`
    }).join(' ')
    return (
        <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="overflow-visible">
            <polyline points={path} fill="none" stroke="var(--color-green-500)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            {/* Optional gradient fill for subtle area */}
            <defs>
                <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="var(--color-green-500)" stop-opacity="0.25" />
                    <stop offset="100%" stop-color="var(--color-green-500)" stop-opacity="0" />
                </linearGradient>
            </defs>
            <polygon points={`${path} ${w},${h} 0,${h}`} fill="url(#sparkFill)" />
        </svg>
    )
}

export const CampaignPerformanceTile = memo(({ campaigns, workspaceId, className = '', loading = false, error = null, failed = false, mock = false }: CampaignPerformanceTileProps) => {
    const { companyId } = useCompany()
    const location = useLocation()

    return (
        <div className={`tile flex flex-col ${className}`.trim()}>
            <div className="flex items-start justify-between mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">Campaign Performance</h3>
                <LineChart className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            {/* Scrollable table wrapper for mobile */}
            <div className="overflow-x-auto -mx-5 px-5">
                {/* Header Row */}
                <div className="grid grid-cols-[minmax(220px,1fr)_80px_120px_minmax(200px,1.2fr)_160px] gap-4 mb-3 px-1 min-w-[780px]">
                    <div className="text-3xs font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400">Campaign</div>
                    <div className="text-3xs font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400">Leads</div>
                    <div className="text-3xs font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400">Companies</div>
                    <div className="text-3xs font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400">Top Web</div>
                    <div className="text-3xs font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400">Trend</div>
                </div>
                {loading && (
                    <ul className="space-y-4 min-w-[780px]">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <li key={i} className="grid grid-cols-[minmax(220px,1fr)_80px_120px_minmax(200px,1.2fr)_160px] items-center gap-4 row-hover">
                                <div className="h-4 w-40 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-4 w-14 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-4 w-20 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-4 w-44 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-10 w-40 rounded bg-shimmer-light animate-shimmer" />
                            </li>
                        ))}
                    </ul>
                )}
                {!loading && error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
                {!loading && !error && failed && (
                    <div className="text-sm text-red-600 dark:text-red-400">Couldn't load campaign performance</div>
                )}
                {!loading && !error && !failed && campaigns && campaigns.length === 0 && (
                    <div className="text-sm text-slate-500 dark:text-slate-400">No active campaigns</div>
                )}
                {!loading && !error && !failed && campaigns && campaigns.length > 0 && (
                    <ul className="space-y-4 min-w-[780px]">
                        {campaigns.map((c: DashboardCampaignPerformanceItemDto) => {
                            // Mock rows carry fabricated ids, and a row cannot navigate without the
                            // company and workspace that build the route.
                            const canNavigate = !mock && !!companyId && !!workspaceId
                            const activation = rowActivation(
                                canNavigate
                                    ? () => location.route(routes.scoped(companyId!, workspaceId).campaign(c.CampaignId))
                                    : null,
                            )
                            return (
                                <li key={c.CampaignId}>
                                  <div
                                    {...activation}
                                    className={`grid grid-cols-[minmax(220px,1fr)_80px_120px_minmax(200px,1.2fr)_160px] items-center gap-4 rounded-md p-2 -m-2 ${activation ? 'row-hover cursor-pointer' : ''}`}
                                  >
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={c.CampaignName}>{c.CampaignName}</div>
                                    </div>
                                    <div className="text-xs2 font-semibold text-green-600 dark:text-green-400" title="Leads Generated">
                                        {c.LeadsGenerated.toLocaleString()}
                                    </div>
                                    <div className="text-xs2 font-medium text-slate-700 dark:text-slate-300" title="Companies Engaged">
                                        {c.CompaniesEngaged.toLocaleString()}
                                    </div>
                                    <div className="text-2xs uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate" title={c.TopWebName || 'No web'}>
                                        {c.TopWebName ? c.TopWebName : '—'}
                                        {c.TopWebLeadCount > 0 && (
                                            <span className="ml-1 text-slate-400 dark:text-slate-500">({c.TopWebLeadCount.toLocaleString()})</span>
                                        )}
                                    </div>
                                    <Sparkline points={c.LeadTrend} />
                                  </div>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </div>
    )
})
